import React from 'react';
import { Button, Grid, Typography, Divider, MenuItem, Avatar } from '@mui/material';
import { Link } from 'react-router-dom';
import voca from 'voca';
import { DateTime } from 'luxon';
import { useAuthContext } from 'src/auth/hooks';
import { Endpoints, useApiRepo } from '../utils/Api';
import { OcdTypeFilterWithAddButton } from '../components/OcdTypeFilterWithAddButton';
import { GenericObsessionTypeCard } from '../components/GenericObsessionTypeCard';
import { useProviderInfo } from '../utils/ProviderInfoContext';

interface IObsessionType {
  id: string;
  name: string;
  description: string | null;
}

interface ITrigger {
  id: string;
  active: boolean;
  created_ts: Date;
  updated_ts: Date;
  trigger: string;
  user_id: string;
  obsession_id: string;
}

interface ICompulsion {
  id: string;
  active: boolean;
  created_ts: Date;
  updated_ts: Date;
  compulsion: string;
  user_id: string;
  obsession_id: string;
}

export interface IObsession {
  id: string;
  description: string;
  daily_time_consumed_seconds: number | null;
  active: boolean;
  user_id: string;
  intensity: number;
  obsession_type: IObsessionType;
  triggers: ITrigger[];
  compulsions: ICompulsion[];
  created_ts: Date;
  updated_ts: Date;
}

export function Profile() {
  const { user } = useAuthContext();
  const { providerInfo } = useProviderInfo();
  const { getObjectsFromAPI } = useApiRepo();

  // Filter Options
  const [obsessions, setObsessions] = React.useState<IObsession[]>([]);
  const [filteredObsessions, setFilteredObsessions] = React.useState<any>([]);

  async function getObsessions() {
    const api = await getObjectsFromAPI();
    const res = await api.get(Endpoints.obessions);
    setObsessions(res.data.data);
    setFilteredObsessions(res.data.data);
  }

  const handleFilters = (displayFilters: any[]) => {
    if (!displayFilters.length) {
      setFilteredObsessions(obsessions);
    } else {
      const filtered = obsessions.filter((obsession: any) =>
        displayFilters.includes(obsession.obsession_type.name)
      );
      setFilteredObsessions(filtered);
    }
  };

  function objectListToString(list: any[], key: string) {
    const objectList = list.map((item) => item[key]);
    return objectList.join(', ');
  }

  async function deleteObsession(obsessionID: string) {
    const api = await getObjectsFromAPI();
    await api.delete(`${Endpoints.obessions}/${obsessionID}`);
  }

  const createCardMenuItems = (obsessionID: string) => {
    const closeMenu = async () => {
      await deleteObsession(obsessionID);
      await getObsessions();
    };
    return [
      <MenuItem key={obsessionID} onClick={closeMenu}>
        Delete
      </MenuItem>
    ];
  };

  React.useEffect(() => {
    getObsessions();
  }, []);

  return (
    <Grid container direction="column" style={{ padding: '20px' }}>
      <Grid item xs={12}>
        <Typography align="left" style={{ fontSize: '36px' }}>
          Hello, {voca.capitalize(user?.first?.trim())}.
        </Typography>
      </Grid>
      <Grid item xs={12} style={{ minHeight: '15px' }}>
        <Divider variant="middle" />
      </Grid>
      <Grid container spacing={1}>
        <Grid item xs={12} lg={1}>
          <Avatar
            alt={user?.first}
            sx={{
              width: 36,
              height: 36,
              border: `solid 2px`,
            }}
          >
            {user?.first?.charAt(0).toUpperCase()}
          </Avatar>
        </Grid>
        <Grid
          item
          xs={12}
          lg={11}
          alignItems="left-center"
          style={{ fontSize: 18, padding: '10px 15px' }}
        >
          <Typography variant="h6" align="left">
            Name: {voca.capitalize(user?.first?.trim())}{' '}
            {voca.capitalize(user?.last?.trim())}
          </Typography>
          <Typography variant="h6" align="left">
            Age:{' '}
            {Math.floor(
              -DateTime.fromFormat(
                user?.dob || '01/01/1900',
                'MM/dd/yyyy'
              ).diffNow('years').years
            )}
          </Typography>
          <Typography variant="h6" align="left">
            Email: {user?.email}
          </Typography>
          {providerInfo && (
            <Typography variant="h6" align="left">
              Provider: {providerInfo?.organization?.name}
            </Typography>
          )}
        </Grid>
      </Grid>
      {!providerInfo && (
        <Grid item>
          <Link to="/checkout">
            <Button
              disableElevation
              variant="contained"
              style={{
                padding: '10px 30px 10px 30px',
                color: '#010d0d',
                border: 'solid 2px #010d0d',
                borderRadius: 30,
                fontSize: '20px',
                backgroundColor: 'transparent'
              }}
            >
              Manage Subscription
            </Button>
          </Link>
        </Grid>
      )}
      <OcdTypeFilterWithAddButton
        onChangeFilter={handleFilters}
        onFormPost={getObsessions}
      >
        <Grid container item spacing={2} justifyContent="center" xs={12}>
          {filteredObsessions.map((obsession: IObsession) => (
            <Grid item key={obsession.id}>
              <GenericObsessionTypeCard
                menuItems={createCardMenuItems(obsession.id)}
                obsessionType={obsession.obsession_type.name}
                cardInfo={[
                  {
                    title: 'T',
                    description: objectListToString(
                      obsession.triggers,
                      'trigger'
                    )
                  },
                  {
                    title: 'O',
                    description: String(obsession.description)
                  },
                  {
                    title: 'C',
                    description: objectListToString(
                      obsession.compulsions,
                      'compulsion'
                    )
                  }
                ]}
              />
            </Grid>
          ))}
        </Grid>
      </OcdTypeFilterWithAddButton>
    </Grid>
  );
}
